import React from 'react';
import classNames from 'classnames';
import type { ApplePay, Card, GooglePay, TokenResult } from '@square/web-payments-sdk-types';
import type { CardInputEvent } from '@square/web-payments-sdk-types/dist/v1/types/payment-method/cards/types';
import type { SqEvent } from '@square/web-payments-sdk-types/dist/v1/types/payment-method/payment-method-types';
import { isNotNullOrUndefined, stringNotEmpty } from 'Util/TypeGuards';
import {
	createPaymentIntent,
	completePayment,
	calculateDynamicSurcharge,
	PaymentProviderTransactionDetails,
} from 'Services/Api/_HumanWritten/PaymentService';
import { usePaymentCheckOut } from 'Views/Components/_HumanWritten/Payment/PaymentCheckoutProvider';
import paymentStore from 'Models/PaymentStore';
import If from 'Views/Components/If/If';
import { LottieSpinner } from '../Lottie/LottieSpinner';
import { useSquarePayments } from './SquareProvider';
import { useCardFieldsFailureReporter } from './PaymentUnavailable';
import { shouldSimulatePaymentFailure } from './paymentFailureSimulation';
import { themeStore } from 'Models/ThemeStore';
import { whiteLabelStore } from 'Models/WhiteLabelStore';
import { store } from 'Models/Store';
import { reportHandledError } from 'Util/_HumanWritten/Observability/observability';
import { logErrorOnServer } from 'Services/Api/_HumanWritten/ClientsideErrorLoggingService';

/**
 * How long `card.attach()` may take before we treat it as never going to finish. Generous, because
 * attaching renders Square's iframes and a slow device or connection can legitimately take seconds;
 * the point is only to have an upper bound at all, not a tight one.
 */
const CARD_ATTACH_TIMEOUT_MS = 15_000;

/**
 * Square-specific payment form. Mirrors <c>StripeCheckoutForm</c> in shape — same imperative
 * <c>submit()</c> method exposed via ref so the parent <c>CheckOutForm</c> can drive both
 * Square and Stripe through the same orchestration.
 *
 * Square's Web Payments SDK is fundamentally different from Stripe Elements:
 *   - Stripe injects a managed iframe and exposes a high-level <c>useStripe()</c> hook.
 *   - Square exposes a low-level <c>card</c> instance you have to manually <c>attach</c> to a
 *     DOM container, and tokenize via <c>card.tokenize()</c>.
 *
 * The form mounts a <c>&lt;div class="square-card-container"&gt;</c> (with a unique per-mount id)
 * and attaches the Square
 * card input to it. On submit, it tokenises and the resulting nonce + card brand are sent
 * to the backend's <c>createPaymentIntent</c> endpoint.
 */
export interface SquareCheckoutFormHandle {
	submit: (savedPaymentMethodId?: string) => Promise<boolean>;
}

export interface SquareCheckoutFormProps {
	tabIsLoading: boolean;
	paymentProcessing: boolean;
	disableContinueButton: boolean;
	/**
	 * Called from inside this component when an Apple Pay or Google Pay payment completes
	 * successfully. The wallet button click flows are entirely self-contained — they do their
	 * own tokenize → createPaymentIntent → completePayment, then call this so the parent can
	 * navigate to the booking-success page (or run its <c>onConfirm</c> hook).
	 *
	 * The <c>confirmAndCheckIn</c> argument is the same flag that
	 * <c>CheckOutForm.handleSubmit</c> passes to <c>onSuccess</c> for the regular card path —
	 * it's the return value of <c>onWalletPaymentBefore</c> (or <c>true</c> if no hook ran).
	 */
	onWalletPaymentSuccess?: (confirmAndCheckIn: boolean) => void;
	/**
	 * Optional pre-payment hook called before the wallet flow starts. Mirrors the
	 * <c>beforePayment</c> step in <c>CheckOutForm.handleSubmit</c> for check-in flows.
	 * Returns a boolean indicating whether the post-payment flow should also mark the booking
	 * as checked-in. If it throws, the wallet payment is aborted and loading state is reset.
	 */
	onWalletPaymentBefore?: () => Promise<boolean>;
}

/**
 * Pull a customer-safe error message out of a failed payment request, but ONLY when the backend
 * flagged it as originating from Square (a card decline / Square API error). Server-side failures
 * — a missing transaction, a DB error, our own guards — are deliberately not surfaced here so we
 * never leak internal error text to the customer; the caller falls back to its own generic message.
 */
const getSquareErrorMessage = (e: any): string | null => {
	const data = e?.response?.data as { message?: string; isSquareError?: boolean } | undefined;
	if (data?.isSquareError === true && stringNotEmpty(data.message)) {
		return data.message;
	}
	return null;
};

// Monotonic per-mount counter used to build unique Square container ids. The Square card (and
// wallet) elements each attach to a DOM container by id. When the form remounts — e.g. staff
// running bookings back-to-back, or a customer toggling between a saved card and a new card —
// the previous mount's card element can still be tearing down while the next mount attaches. On
// a *shared* container id those two overlap on the same DOM node / SDK slot, corrupting Square's
// state so the next `card.tokenize()` throws an opaque UNKNOWN and `verifyBuyer` (card-on-file
// save) silently fails. A distinct id per mount keeps every card instance on its own container so
// they can never collide. A plain counter is enough — ids only need to be unique within the page,
// and `useId()` isn't usable here because its colons aren't valid in a CSS/`attach()` selector.
let squareContainerInstanceCounter = 0;
const nextSquareContainerInstanceId = (): number => {
	squareContainerInstanceCounter += 1;
	return squareContainerInstanceCounter;
};

const SquareCheckoutForm = React.forwardRef<SquareCheckoutFormHandle, SquareCheckoutFormProps>(
	({ tabIsLoading, paymentProcessing, onWalletPaymentSuccess, onWalletPaymentBefore }, ref) => {
		const squarePayments = useSquarePayments();
		// Null if this form is ever rendered outside SquareProvider; the optional calls below then
		// report nowhere rather than throwing on a payment screen.
		const cardFieldsFailure = useCardFieldsFailureReporter();
		const cardRef = React.useRef<Card | null>(null);
		const applePayRef = React.useRef<ApplePay | null>(null);
		const googlePayRef = React.useRef<GooglePay | null>(null);
		// Unique per-mount container ids (see nextSquareContainerInstanceId above). Held in a ref so
		// they stay stable across re-renders of this instance but differ between mounts.
		const containerInstanceIdRef = React.useRef<number>();
		if (containerInstanceIdRef.current === undefined) {
			containerInstanceIdRef.current = nextSquareContainerInstanceId();
		}
		const containerInstanceId = containerInstanceIdRef.current;
		const cardContainerId = `square-card-container-${containerInstanceId}`;
		const applePayContainerId = `square-apple-pay-button-${containerInstanceId}`;
		const googlePayContainerId = `square-google-pay-button-${containerInstanceId}`;

		// Wallet button availability state. Set to true once each wallet has successfully
		// initialised and attached. Used to conditionally render the buttons — if the user's
		// browser/device doesn't support a wallet, its button doesn't render at all.
		const [applePayAvailable, setApplePayAvailable] = React.useState(false);
		const [googlePayAvailable, setGooglePayAvailable] = React.useState(false);

		// Local loading flag covering Square SDK fetch + card.attach(). Kept separate from the
		// shared tabIsLoading so the card container can stay in normal flow during attach —
		// applying the .hidden class (max-height: 0) while Square is attaching causes its
		// iframe to render at the wrong size.
		const [squareAttaching, setSquareAttaching] = React.useState(true);

		// Stash the latest props in refs so the wallet click handlers (which are set up once
		// inside useEffect) always read the current callbacks rather than stale closures.
		const onWalletPaymentSuccessRef = React.useRef(onWalletPaymentSuccess);
		const onWalletPaymentBeforeRef = React.useRef(onWalletPaymentBefore);
		React.useEffect(() => {
			onWalletPaymentSuccessRef.current = onWalletPaymentSuccess;
			onWalletPaymentBeforeRef.current = onWalletPaymentBefore;
		}, [onWalletPaymentSuccess, onWalletPaymentBefore]);

		const {
			handleError,
			setRefetchPrice,
			refetchPrice,
			setTabIsLoading,
			setPaymentProcessing,
			setMessage,
			handleSurchargeUpdate,
			handleProviderNotLoaded,
			transactionId,
			userId,
			eventBooking,
			inCheckIn,
			message,
			transactionDetails,
			totalPriceWithSurcharge,
		} = usePaymentCheckOut();

		// Track which card fields currently have errors so we can build a combined message.
		const fieldErrorsRef = React.useRef<Set<string>>(new Set());

		// Last brand we successfully recalculated the surcharge against. Square's `cardBrandChanged`
		// event can fire repeatedly while the user types (BIN lookups update as digits are entered);
		// this lets us skip duplicate calls for the same brand.
		const lastSurchargeBrandRef = React.useRef<string | null>(null);
		const surchargeDebounceRef = React.useRef<ReturnType<typeof setTimeout> | null>(null);

		const recalcSurchargeForBrand = React.useCallback(
			(brand: string) => {
				if (!stringNotEmpty(transactionId)) return;
				const normalised = brand.toUpperCase();
				if (lastSurchargeBrandRef.current === normalised) return;
				lastSurchargeBrandRef.current = normalised;

				// Square only knows the brand once enough digits are entered, and the event can fire a
				// few times in quick succession as the user finishes typing. Debounce so we only post
				// once per stable brand.
				if (surchargeDebounceRef.current) clearTimeout(surchargeDebounceRef.current);
				surchargeDebounceRef.current = setTimeout(async () => {
					try {
						const result = await calculateDynamicSurcharge(transactionId, eventBooking, normalised);
						if (result.hasChanged) {
							handleSurchargeUpdate(result.refresh);
						} else {
							setRefetchPrice(!refetchPrice);
						}
					} catch {
						// Surcharge recompute is best-effort — a failure here shouldn't block the user
						// from completing the payment. The backend will recompute again at payment-creation.
					}
				}, 250);
			},
			[transactionId, eventBooking, handleSurchargeUpdate, setRefetchPrice, refetchPrice],
		);

		React.useEffect(
			() => () => {
				if (surchargeDebounceRef.current) clearTimeout(surchargeDebounceRef.current);
			},
			[],
		);

		const fieldNameMap: Record<string, string> = {
			cardNumber: 'Card number',
			cvv: 'CVV',
			expirationDate: 'Expiration date',
			postalCode: 'Postal code',
		};

		const buildErrorMessage = (fields: Set<string>): string => {
			const names = Array.from(fields).map(f => fieldNameMap[f] ?? f);
			if (names.length === 1) return `${names[0]} is invalid.`;
			return `${names.join(', ')} are invalid.`;
		};

		// Capture the underlying Square tokenisation failure. Square surfaces an opaque
		// "Tokenisation has failed. An unknown error has occurred." to the customer, but the
		// TokenResult / thrown error carries an errors[] array (type / code / field / message)
		// that distinguishes a config issue (e.g. INVALID_APPLICATION_ID) from a blocked-request
		// or transient failure. Without this the error is undiagnosable after the fact.
		const reportTokenizeFailure = (
			context: string,
			detail: { status?: string; errors?: unknown; error?: unknown },
		) => {
			// A failed tokenize() produces no Square token and no request id, so there is nothing
			// Square-issued to quote. This correlation id, together with the Square application id,
			// location id and timestamp below, is the strongest traceable handle we can hand Square
			// to locate a single occurrence in their telemetry. It is logged to both Sentry/Faro and
			// the server log so the two streams line up on one id.
			const correlationId =
				typeof crypto !== 'undefined' && 'randomUUID' in crypto
					? crypto.randomUUID()
					: `sq-${Date.now()}-${Math.random().toString(36).slice(2)}`;
			const squareApplicationId = paymentStore.publishableKey ?? 'unknown';
			const squareLocationId = paymentStore.accountId ?? 'unknown';
			const occurredAt = new Date().toISOString();

			const squareErrors = Array.isArray(detail.errors)
				? (detail.errors as Array<{ type?: string; code?: string; field?: string; message?: string }>)
				: undefined;
			const errorSummary = squareErrors
				?.map(e => [e.type, e.code, e.field, e.message].filter(Boolean).join(':'))
				.join(' | ');

			// Fold status + the Square error summary into the message string itself. Faro's
			// console-capture coerces extra args to text and joins them, so an object second arg
			// lands in logs as "[object Object]" and the useful fields are lost. The detail object is
			// still passed for local devtools inspection.
			// One self-contained string — no object second argument. Console-capture instrumentation
			// (Faro) coerces any non-string console argument to text and appends it, so passing the
			// structured detail object here lands in captured logs as the useless "[object Object]"
			// and the real fields are lost. Everything diagnosable is folded into the message; the
			// structured object still goes to Sentry/Faro via reportHandledError below.
			const thrownMessage =
				detail.error instanceof Error ? `${detail.error.name}: ${detail.error.message}` : undefined;
			console.error(
				`[SquareCheckoutForm] ${context} tokenisation failed ` +
					`(correlationId: ${correlationId}, status: ${detail.status ?? 'thrown'}, ` +
					`errors: ${errorSummary ?? 'none'}` +
					`${thrownMessage ? `, thrown: ${thrownMessage}` : ''})`,
			);

			// Browser-side observability (Sentry/GlitchTip + Faro) — gets everything, incl. routine
			// validation failures, which those tools group/dedupe well.
			reportHandledError(detail.error ?? new Error(`Square ${context} tokenisation failed`), {
				event: 'square_tokenize_failure',
				attributes: {
					context,
					correlationId,
					status: detail.status ?? 'thrown',
					squareErrors: errorSummary ?? 'none',
					environment: paymentStore.isSandbox ? 'sandbox' : 'production',
					// Square-side lookup keys: with no token/request id on a failed tokenize, the app id
					// + location id + timestamp are what Square can search their telemetry by.
					squareApplicationId,
					squareLocationId,
					occurredAt,
					transactionId: transactionId ?? 'unknown',
				},
			});

			// Server (Serilog) log — only for genuinely unexpected failures (the "unknown error"
			// class), not routine card-validation typos or user-cancelled wallet sheets, so the
			// server logs stay signal-heavy. Always send a synthesised stack: the endpoint drops
			// any report missing a message or stack.
			const routineStatuses = ['Invalid', 'Cancel', 'Abort'];
			const isRoutineFailure = detail.status !== undefined && routineStatuses.includes(detail.status);
			if (!isRoutineFailure) {
				const message =
					`Square ${context} tokenisation failed ` +
					`(correlationId: ${correlationId}, status: ${detail.status ?? 'thrown'}, ` +
					`env: ${paymentStore.isSandbox ? 'sandbox' : 'production'}, ` +
					`squareApplicationId: ${squareApplicationId}, squareLocationId: ${squareLocationId}, ` +
					`occurredAt: ${occurredAt}, transaction: ${transactionId ?? 'unknown'}). ` +
					`Square errors: ${errorSummary ?? 'none'}`;
				const stack =
					(detail.error instanceof Error && detail.error.stack) || errorSummary || message;
				logErrorOnServer({
					loggedInUserId: store.userId ?? undefined,
					currentLocation: document.location.href.toString(),
					previousLocation: document.referrer,
					message,
					stack,
				}).catch(() => {
					// Best-effort — the endpoint swallows non-2xx (validateStatus: () => true); this
					// just guards a network reject so logging never breaks the payment flow.
				});
			}
		};

		// Mount the Square card input as soon as the SDK instance is available.
		React.useEffect(() => {
			let cancelled = false;
			let card: Card | null = null;

			setSquareAttaching(true);

			async function mount() {
				if (!squarePayments) {
					return;
				}
				// Dev-only, armed from sessionStorage — see paymentFailureSimulation.
				if (shouldSimulatePaymentFailure('element')) {
					cardFieldsFailure?.reportElementFailure();
					setSquareAttaching(false);
					return;
				}
				try {
					// In sandbox mode, pre-fill a US ZIP so the US-issued test cards pass
					// AVS validation without manual input. In production, omit postalCode
					// so Square renders its own postal code field — it automatically adapts
					// to the card's issuing country (e.g. 4-digit AU postcode, 5-digit US
					// ZIP) and hides itself when the issuer doesn't require one.
					const brandPrimary = themeStore.config.brandColourPrimary || '#151517';
					card = await squarePayments.card({
						...(paymentStore.isSandbox ? { postalCode: '94103' } : {}),
						style: {
							'.input-container': {
								borderColor: '#d9d9d9',
								borderRadius: '8px',
							},
							'.input-container.is-focus': {
								borderColor: brandPrimary,
							},
							'.input-container.is-error': {
								borderColor: '#d32f2f',
							},
							// TODO: Hide Square's built-in error messages once comparison is done.
							// Set colors to 'transparent' to hide them.
							'.message-text': {
								color: '#d32f2f',
							},
							'.message-text.is-error': {
								color: '#d32f2f',
							},
							'.message-icon': {
								color: '#d32f2f',
							},
							'.message-icon.is-error': {
								color: '#d32f2f',
							},
							input: {
								backgroundColor: '#ffffff',
								fontSize: '14px',
							},
							'input::placeholder': {
								color: '#999999',
							},
						},
					});
					if (cancelled) {
						await card.destroy();
						return;
					}
					// Bounded on purpose. `attach()` rejects on a bad applicationId / locationId, but a
					// request that is blackholed rather than refused never settles at all — and this
					// effect's only other exit sets `squareAttaching` false, so an attach that simply
					// never returns left the spinner running for the life of the page with nothing to
					// act on. Same failure the provider's SDK timeout exists to stop, one level down.
					let attachTimer: ReturnType<typeof setTimeout> | undefined;
					try {
						await Promise.race([
							card.attach(`#${cardContainerId}`),
							new Promise<never>((_, reject) => {
								attachTimer = setTimeout(
									() => reject(new Error('Square card input did not attach in time')),
									CARD_ATTACH_TIMEOUT_MS,
								);
							}),
						]);
					} finally {
						// Cleared whichever side won. Left running, the loser's timer holds this
						// closure — and the effect's own container id with it — for another 15
						// seconds after a perfectly successful attach.
						clearTimeout(attachTimer);
					}
					cardRef.current = card;

					// Listen for field-level validation errors and surface them via the
					// same InformationBox error UI that Stripe uses.
					card.addEventListener('errorClassAdded', (event: SqEvent<CardInputEvent>) => {
						if (cancelled) return;
						const field = event.detail?.field;
						if (field) {
							fieldErrorsRef.current.add(field);
							handleError(buildErrorMessage(fieldErrorsRef.current));
						}
					});
					card.addEventListener('errorClassRemoved', (event: SqEvent<CardInputEvent>) => {
						if (cancelled) return;
						const field = event.detail?.field;
						if (field) {
							fieldErrorsRef.current.delete(field);
							if (fieldErrorsRef.current.size === 0) {
								setMessage(null);
							} else {
								handleError(buildErrorMessage(fieldErrorsRef.current));
							}
						}
					});

					// Square's nonce is opaque server-side, so the only chance the backend has to
					// pick a brand-aware surcharge percentage is to receive the brand from the
					// frontend. Listen for cardBrandChanged so we can recompute the displayed
					// surcharge before the user submits.
					card.addEventListener('cardBrandChanged', (event: SqEvent<CardInputEvent>) => {
						if (cancelled) return;
						const brand = (event.detail as { cardBrand?: string } | undefined)?.cardBrand;
						if (stringNotEmpty(brand) && brand !== 'unknown') {
							recalcSurchargeForBrand(brand);
						}
					});

					setTabIsLoading(false);
					setSquareAttaching(false);
				} catch (e) {
					console.error('Failed to mount Square card input:', e);
					// Reported upward, not through handleError. handleError writes a message into
					// THIS form, and reporting unmounts it — so the message was being set on a
					// component about to disappear, and its "Please refresh" was the only way out.
					// Through the card-fields contract the slot shows the error with a Try again,
					// and every other payment method stays selectable behind it.
					setTabIsLoading(false);
					setSquareAttaching(false);
					cardFieldsFailure?.reportElementFailure();
				}
			}

			mount().catch(() => {
				/* errors are surfaced inside mount() */
			});
			return () => {
				cancelled = true;
				fieldErrorsRef.current.clear();
				if (card) {
					// Immediate teardown. Each mount owns a unique container id (see above), so this
					// destroy can't race the next mount's card.attach() on a shared DOM node — which is
					// why the teardown no longer needs to be serialised across mounts.
					card.destroy().catch(() => {
						/* ignore */
					});
					cardRef.current = null;
				}
			};
			// eslint-disable-next-line react-hooks/exhaustive-deps
		}, [squarePayments, cardFieldsFailure]);

		const handleErrorLocal = (errorMessage: string | null) => {
			handleError(errorMessage);
			setTabIsLoading(false);
		};

		/**
		 * Mount the Apple Pay and Google Pay wallet buttons. Re-runs whenever the total price
		 * changes because Square's wallet instances bake the amount into a PaymentRequest object
		 * and have no way to update it in place — we have to destroy and recreate.
		 *
		 * Either wallet can fail to instantiate (e.g. unsupported browser, no payment methods
		 * configured on the device, dashboard not enabled). In that case we just don't render its
		 * button — the card input remains as the fallback.
		 */
		React.useEffect(() => {
			let cancelled = false;
			let applePay: ApplePay | null = null;
			let googlePay: GooglePay | null = null;

			async function mountWallets() {
				if (!squarePayments) {
					return;
				}
				if (!totalPriceWithSurcharge || totalPriceWithSurcharge <= 0) {
					return;
				}
				// Staff/admin users take payments on behalf of customers — they're never paying with
				// their own wallet, so hide Apple Pay and Google Pay to avoid accidental charges on
				// a staff member's device.
				if (store.isStaff) {
					return;
				}

				const currencyCode = paymentStore.currencyCode || paymentStore.currency || 'AUD';
				const paymentRequestConfig = {
					countryCode: whiteLabelStore.countryCode,
					currencyCode,
					total: {
						amount: totalPriceWithSurcharge.toFixed(2),
						label: 'Total',
					},
				};
				const buildPaymentRequest = () => squarePayments.paymentRequest(paymentRequestConfig);

				// ─── Apple Pay ───────────────────────────────────────────────────
				try {
					applePay = await squarePayments.applePay(buildPaymentRequest());
					if (cancelled) {
						await applePay.destroy();
						return;
					}
					applePayRef.current = applePay;
					setApplePayAvailable(true);

					// Apple Pay has no .attach() — we wire our own click handler on the
					// button container element.
					const applePayContainer = document.getElementById(applePayContainerId);
					if (applePayContainer) {
						applePayContainer.onclick = async event => {
							event.preventDefault();
							await processWalletPayment('apple-pay', applePayRef.current);
						};
					}
				} catch {
					// Apple Pay not available on this device/browser — card input remains as fallback.
				}

				if (cancelled) return;

				// ─── Google Pay ──────────────────────────────────────────────────
				try {
					googlePay = await squarePayments.googlePay(buildPaymentRequest());
					if (cancelled) {
						await googlePay.destroy();
						return;
					}
					await googlePay.attach(`#${googlePayContainerId}`, {
						buttonColor: 'white',
						buttonSizeMode: 'fill',
					});
					googlePayRef.current = googlePay;
					setGooglePayAvailable(true);

					// Square renders Google's official button via .attach(), but we still have
					// to call .tokenize() ourselves on click. Wire a click handler on the
					// container element (the button is a child of it).
					const googlePayContainer = document.getElementById(googlePayContainerId);
					if (googlePayContainer) {
						googlePayContainer.onclick = async event => {
							event.preventDefault();
							await processWalletPayment('google-pay', googlePayRef.current);
						};
					}
				} catch (e) {
					console.error('[SquareCheckoutForm] Google Pay not available:', e);
				}
			}

			mountWallets().catch(() => {
				/* errors surfaced inside */
			});
			return () => {
				cancelled = true;
				if (applePay) {
					applePay.destroy().catch(() => {
						/* ignore */
					});
					applePayRef.current = null;
					setApplePayAvailable(false);
				}
				if (googlePay) {
					googlePay.destroy().catch(() => {
						/* ignore */
					});
					googlePayRef.current = null;
					setGooglePayAvailable(false);
				}
			};
			// processWalletPayment is stable via the refs above, so we don't need it in deps.
			// eslint-disable-next-line react-hooks/exhaustive-deps
		}, [squarePayments, totalPriceWithSurcharge]);

		/**
		 * Self-contained wallet payment flow. Runs the same backend sequence as the regular
		 * card submit (createPaymentIntent → completePayment) but driven from the wallet button
		 * click rather than the parent form's submit handler. On success, calls the parent's
		 * <c>onWalletPaymentSuccess</c> prop so it can navigate to the booking-success page.
		 */
		const processWalletPayment = async (
			walletName: string,
			walletInstance: ApplePay | GooglePay | null,
		): Promise<void> => {
			if (!walletInstance) {
				return;
			}
			setMessage(null);
			setPaymentProcessing(true);
			setTabIsLoading(true);

			// Run the optional pre-payment hook (e.g. check-in confirmation modal). Captures the
			// confirmAndCheckIn boolean that's passed to onWalletPaymentSuccess on completion,
			// matching how the regular card path threads beforePayment's return value.
			let confirmAndCheckIn = true;
			if (onWalletPaymentBeforeRef.current) {
				try {
					confirmAndCheckIn = await onWalletPaymentBeforeRef.current();
				} catch {
					setPaymentProcessing(false);
					setTabIsLoading(false);
					return;
				}
			}

			try {
				const tokenResult: TokenResult = await walletInstance.tokenize();
				if (tokenResult.status !== 'OK') {
					reportTokenizeFailure(walletName, {
						status: tokenResult.status,
						errors: (tokenResult as { errors?: unknown }).errors,
					});
					const errors = (tokenResult as { errors?: { message: string }[] }).errors;
					const errorMessage =
						Array.isArray(errors) && errors.length > 0
							? errors.map(e => e.message).join('; ')
							: 'Payment was cancelled or could not be completed. Please try again.';
					handleErrorLocal(errorMessage);
					return;
				}

				// Wallet payments don't need verifyBuyer — the wallet itself is the buyer
				// attestation (Touch ID / Face ID / Google biometric). Card-on-file save isn't
				// supported for wallet sources anyway.
				const intent = await createNewPaymentIntent(
					tokenResult.token,
					tokenResult.details?.card?.brand,
					undefined,
					undefined,
				);
				if (!isNotNullOrUndefined(intent)) {
					// The error message was already surfaced inside createNewPaymentIntent's
					// catch block (handleError / handleSurchargeUpdate). Just bail out.
					return;
				}

				await completePayment(intent.paymentIntentId, inCheckIn);

				// Navigate to success immediately. Don't reset paymentProcessing/tabIsLoading
				// here — the navigation will unmount this component. Resetting state before
				// the navigation renders would trigger a booking wizard re-render whose
				// step-validation guard could redirect back to an earlier tab.
				if (onWalletPaymentSuccessRef.current) {
					onWalletPaymentSuccessRef.current(confirmAndCheckIn);
				}
			} catch (e: any) {
				console.error(`[SquareCheckoutForm] ${walletName} payment flow failed:`, e);
				handleErrorLocal(getSquareErrorMessage(e) ?? `${walletName} payment failed. Please try again.`);
			}
		};

		/**
		 * Tokenises the card and extracts just the token + brand we need. Square's TokenResult is
		 * a discriminated union and TS narrowing across function boundaries gets messy, so we
		 * collapse it to a simple shape here.
		 */
		type TokenizeAttempt =
			| { ok: true; token: string; cardBrand?: string }
			| { ok: false; retryable: boolean; result?: TokenResult; error?: any };

		// A single tokenisation attempt. `retryable` marks the transient/race class — a thrown error
		// or an `Unknown`/`Error` status with no tracked field errors (Square's opaque UNKNOWN). Real
		// validation problems (field errors, or `Invalid` status) are never retryable — a second try
		// won't fix a mistyped card.
		const tokenizeCardOnce = async (): Promise<TokenizeAttempt> => {
			if (!cardRef.current) {
				return { ok: false, retryable: false };
			}
			try {
				const result: TokenResult = await cardRef.current.tokenize();
				if (result.status === 'OK') {
					return { ok: true, token: result.token ?? '', cardBrand: result.details?.card?.brand };
				}
				const retryable =
					fieldErrorsRef.current.size === 0 &&
					(result.status === 'Unknown' || result.status === 'Error');
				return { ok: false, retryable, result };
			} catch (error: any) {
				return { ok: false, retryable: fieldErrorsRef.current.size === 0, error };
			}
		};

		const tokenize = async (): Promise<{ token: string; cardBrand?: string } | null> => {
			if (!cardRef.current) {
				handleProviderNotLoaded();
				return null;
			}

			let attempt = await tokenizeCardOnce();

			// One automatic retry for the transient/race class. An overlapping card teardown from a
			// previous booking (or a transient network blip reaching Square) can leave tokenisation
			// momentarily failing with an opaque UNKNOWN; a second attempt on the same card clears it,
			// so staff never see the error. Skipped for real validation failures.
			if (!attempt.ok && attempt.retryable) {
				await new Promise(resolve => {
					setTimeout(resolve, 300);
				});
				if (cardRef.current) {
					attempt = await tokenizeCardOnce();
				}
			}

			if (attempt.ok) {
				return { token: attempt.token, cardBrand: attempt.cardBrand };
			}

			// Non-OK status path (after any retry): report + surface the message.
			if (attempt.result) {
				reportTokenizeFailure('card', {
					status: attempt.result.status,
					errors: (attempt.result as { errors?: unknown }).errors,
				});
				// If we already have field-level errors tracked from the errorClassAdded
				// events, use those for a specific message. Otherwise fall back to
				// whatever Square returned in its errors array.
				if (fieldErrorsRef.current.size > 0) {
					handleErrorLocal(buildErrorMessage(fieldErrorsRef.current));
					return null;
				}
				const errors = (attempt.result as { errors?: { message: string }[] }).errors;
				const errorMessage =
					Array.isArray(errors) && errors.length > 0
						? errors.map(e => e.message).join('; ')
						: 'Please check your card details and try again.';
				handleErrorLocal(errorMessage);
				return null;
			}

			// Thrown path (after any retry).
			const thrown = attempt.error;
			reportTokenizeFailure('card', { error: thrown, errors: thrown?.errors });
			if (fieldErrorsRef.current.size > 0) {
				handleErrorLocal(buildErrorMessage(fieldErrorsRef.current));
			} else {
				handleErrorLocal(thrown?.message ?? 'An unexpected error occurred while processing the card.');
			}
			return null;
		};

		const createNewPaymentIntent = async (
			nonce?: string,
			cardBrand?: string,
			savedPaymentMethodId?: string,
			verificationToken?: string,
		): Promise<PaymentProviderTransactionDetails | null> => {
			try {
				const response = await createPaymentIntent(
					transactionId,
					transactionDetails?.formActionUrl ?? '',
					eventBooking,
					inCheckIn,
					undefined, // confirmationTokenId is Stripe-only
					savedPaymentMethodId,
					nonce,
					cardBrand,
					verificationToken,
				);
				return response.data as PaymentProviderTransactionDetails;
			} catch (e: any) {
				const errorResponse = e?.response?.data as unknown as { message: string; refresh: boolean };
				if (errorResponse?.message?.includes('Your selected payment method has updated the surcharge')) {
					handleSurchargeUpdate(errorResponse.refresh);
				} else {
					// Show the Square decline message when the backend tagged this as a Square error;
					// otherwise keep it generic so we don't expose internal server errors.
					handleError(getSquareErrorMessage(e) ?? 'An unexpected error occurred.');
				}
				setRefetchPrice(!refetchPrice);
				return null;
			}
		};

		/**
		 * Calls Square's payments.verifyBuyer to obtain a verification token. Required when
		 * saving a card to Square Card on File (Square's 3DS-style buyer attestation).
		 *
		 * Returns null on failure — the caller falls through and the payment proceeds as a
		 * one-off charge without saving the card. Skipped entirely for guest checkouts (no
		 * userId), since there's no customer to save the card against.
		 */
		const verifyBuyerForSave = async (nonce: string): Promise<string | null> => {
			if (!stringNotEmpty(userId)) {
				return null;
			}
			if (!squarePayments) {
				return null;
			}
			try {
				const verificationDetails = {
					intent: 'STORE' as const,
					billingContact: {},
				};
				const result = await squarePayments.verifyBuyer(nonce, verificationDetails);
				if (!stringNotEmpty(result?.token)) {
					return null;
				}
				return result.token;
			} catch (e) {
				// verifyBuyer failed — proceed without saving the card. Report it so it's diagnosable
				// (this was previously a fully-silent swallow).
				reportTokenizeFailure('verifyBuyer', { error: e, errors: (e as { errors?: unknown })?.errors });
				return null;
			}
		};

		React.useImperativeHandle(ref, () => ({
			submit: async (savedPaymentMethodId?: string) => {
				if (savedPaymentMethodId) {
					// Saved card flow — Square uses the card-on-file ID directly as the source.
					if (message !== null) {
						handleProviderNotLoaded();
						return false;
					}
					const intent = await createNewPaymentIntent(undefined, undefined, savedPaymentMethodId);
					if (!isNotNullOrUndefined(intent)) {
						handleProviderNotLoaded();
						return false;
					}
					try {
						await completePayment(intent.paymentIntentId, inCheckIn);
						return true;
					} catch (e) {
						handleErrorLocal(
							getSquareErrorMessage(e) ??
								'Payment was processed but an error occurred. Please contact support.',
						);
						return false;
					}
				}

				// Fresh card flow.
				const tokenResult = await tokenize();
				if (!tokenResult) {
					// tokenize() already called handleErrorLocal on its error paths, but reset
					// loading defensively if we somehow got here without an error surfacing.
					handleProviderNotLoaded();
					return false;
				}
				if (message !== null) {
					handleProviderNotLoaded();
					return false;
				}

				// Authenticated users get their card saved to Square Card on File for next time.
				// verifyBuyer obtains the 3DS-style attestation token Square requires for saving.
				// Guests skip this entirely (verifyBuyerForSave returns null).
				const verificationToken = await verifyBuyerForSave(tokenResult.token);

				const intent = await createNewPaymentIntent(
					tokenResult.token,
					tokenResult.cardBrand,
					undefined,
					verificationToken ?? undefined,
				);
				if (!isNotNullOrUndefined(intent)) {
					handleProviderNotLoaded();
					return false;
				}

				// Square's Payments API completes the payment server-side as soon as the nonce is
				// exchanged for a Payment (no client-side confirmation step like Stripe). At this
				// point the backend has already captured the funds — call completePayment to mark
				// the booking as completed.
				try {
					await completePayment(intent.paymentIntentId, inCheckIn);
					return true;
				} catch (e) {
					handleErrorLocal(
						getSquareErrorMessage(e) ??
							'Payment was processed but an error occurred. Please contact support.',
					);
					return false;
				}
			},
		}));

		const showAnyWallet = applePayAvailable || googlePayAvailable;

		const showLoadingOverlay = squareAttaching || tabIsLoading;

		return (
			<div id="payment-container" className={classNames('square', { loading: showLoadingOverlay })}>
				<div
					id={googlePayContainerId}
					style={{
						width: '100%',
						borderRadius: '8px',
						overflow: 'hidden',
					}}
				/>
				<div
					id={applePayContainerId}
					style={{
						display: applePayAvailable ? 'block' : 'none',
						height: '48px',
						width: '100%',
						borderRadius: '8px',
						WebkitAppearance: '-apple-pay-button' as any,
						// @ts-expect-error
						ApplePayButtonType: 'plain',
						ApplePayButtonStyle: 'white-outline',
						cursor: 'pointer',
					}}
				/>
				<If condition={showAnyWallet}>
					<div
						className="square-wallet-divider"
						style={{
							display: 'flex',
							alignItems: 'center',
							margin: '12px 0',
							color: '#888',
							fontSize: '0.85em',
							lineHeight: '1',
						}}
					>
						<div style={{ flex: 1, borderBottom: '1px solid #ddd' }} />
						<span style={{ padding: '0 12px', whiteSpace: 'nowrap' }}>or pay with card</span>
						<div style={{ flex: 1, borderBottom: '1px solid #ddd' }} />
					</div>
				</If>
				<div id={cardContainerId} className="square-card-container" />
				<If condition={showLoadingOverlay}>
					<div className="square-loading-overlay">
						<LottieSpinner />
					</div>
				</If>
			</div>
		);
	},
);

export default SquareCheckoutForm;
